import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type ReactEventHandler, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { nestleTariffList } from 'reactApp/appHelpers/featuresHelpers/features/nestle';
import { useMinWidthBreakpoint } from 'reactApp/hooks/responsiveness/useMinWidthBreakpoint';
import { initProducts, initProductsForAnonymous } from 'reactApp/modules/products/products.module';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { NormalizedTariffByProductIds } from 'reactApp/modules/products/products.types';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { PrizeCard } from 'reactApp/sections/Nestle/PrizeCard/PrizeCard';
import { TariffNestleCard } from 'reactApp/sections/Nestle/TariffsBlock/TariffCard';
import { TariffSwitch } from 'reactApp/sections/QuotaLanding/MultiTariffsList/TariffPeriodSwitch/TariffSwitch';
import { NavButtons } from 'reactApp/sections/QuotaLanding/TariffsList/NavButtons/NavButtons';
import type { RootState } from 'reactApp/store';
import type { SwiperNavigator } from 'reactApp/types/SwiperNavigator';
import type { SwitchOption } from 'reactApp/ui/TariffCardNew/components/TariffSwitch.types';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { type Swiper as SwiperInstance, Mousewheel, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './TariffsBlock.css';

const switchOptions: [SwitchOption, SwitchOption] = [
    {
        value: ETabsName.month,
        text: 'На месяц',
    },
    {
        value: ETabsName.year,
        text: 'На год',
    },
];

export const TariffsBlock = forwardRef<HTMLDivElement | null, { onSuccessPayment: () => void }>(({ onSuccessPayment }, ref) => {
    const dispatch = useDispatch();

    const isMobile = useSelector(QuotaLandingSelectors.isMobile);

    const tariffList = useSelector((state: RootState) => ProductsSelectors.getNestleTariffsList(state, nestleTariffList, 'nestle'));

    const isAnonymous = useSelector(UserSelectors.isAnonymous);

    const sortedTariffList = useMemo(() => {
        return nestleTariffList.year
            .map((id) => tariffList.find((tariff) => tariff.products.yearProduct.id === id))
            .filter((item) => Boolean(item)) as NormalizedTariffByProductIds[];
    }, [tariffList]);

    const [activePeriodTab, setActivePeriodTab] = useState<ETabsName>(ETabsName.year);
    const [navState, setNavState] = useState({ isStart: true, isLast: false });
    const [navigator, setNavigator] = useState<SwiperNavigator | null>(null);

    const swiperRef = useRef<SwiperInstance>();

    const cardRef = useRef<HTMLDivElement>(null);
    const lgBreackPoint = cardRef?.current ? sortedTariffList?.length * ((cardRef.current?.scrollWidth ?? 0) + 20) : 0;

    const updateNavState = useCallback(() => {
        setNavState({ isStart: navigator?.isFirst() as boolean, isLast: navigator?.isLast() as boolean });
    }, [navigator, setNavState]);

    useEffect(() => {
        updateNavState();
    }, [navigator, updateNavState]);

    const handleSetNavigator = useCallback(
        (navigator) => {
            setNavigator(navigator);
        },
        [setNavigator, updateNavState]
    );

    const [lgBreakpointHit, breackPoint, setBreackPoint] = useMinWidthBreakpoint(lgBreackPoint);
    const displayInSwiper = !lgBreakpointHit;

    const onTariffSwitchClick: ReactEventHandler = useCallback(
        (event) => {
            event.stopPropagation();
            setActivePeriodTab(activePeriodTab === ETabsName.year ? ETabsName.month : ETabsName.year);
        },
        [activePeriodTab]
    );

    useEffect(() => {
        if (!breackPoint) {
            setBreackPoint(lgBreackPoint);
        }
    }, [lgBreackPoint]);

    useEffect(() => {
        if (isAnonymous) {
            dispatch(initProductsForAnonymous());
        }
        dispatch(initProducts());
    }, []);

    return (
        <div
            className={classNames(styles.root, {
                [styles.root_mobile]: isMobile,
            })}
            ref={ref}
        >
            <Spacing size={isMobile ? 0 : 100} />
            <div className={styles.infoBlock}>
                <PrizeCard title="4 000 ₽" isMobile={isMobile} size={isMobile ? 'large' : 'small'} />
                <div className={styles.textBlock}>
                    <div className={styles.title}>Приз побольше —&nbsp; подпиской Mail Space</div>
                    <Spacing size={16} />
                    <div className={styles.description}>
                        Выберите тариф и у вас будут: безлимитная память* для фото любимок с телефона
                        <br />и шанс выиграть сертификат на 4 000 ₽
                    </div>
                </div>
            </div>
            {isMobile ? (
                <>
                    <Spacing size={24} />
                    <div className={styles.mobileSwiper}>
                        <Swiper
                            slidesPerView={'auto'}
                            pagination={{
                                clickable: true,
                                bulletClass: styles.bullet,
                                bulletActiveClass: styles.bulletActive,
                                clickableClass: styles.bulletWrapper,
                            }}
                            modules={[Navigation, Pagination]}
                            spaceBetween={8}
                        >
                            {sortedTariffList.map((tariff) => (
                                <SwiperSlide key={`${tariff.tariffId}`} className={styles.slide}>
                                    <TariffNestleCard
                                        tariff={tariff}
                                        isYear={activePeriodTab === ETabsName.year}
                                        isMobile
                                        onSuccessPayment={onSuccessPayment}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </>
            ) : (
                <>
                    <Spacing size={40} />
                    <div className={styles.controls}>
                        <TariffSwitch
                            dark
                            checked={activePeriodTab === ETabsName.year}
                            onClick={onTariffSwitchClick}
                            options={switchOptions}
                            isRebrandingQuotaLanding
                            gray
                        />
                        {displayInSwiper && (
                            <div className={styles.navButtons}>
                                <NavButtons
                                    isLast={navState.isLast}
                                    isStart={navState.isStart}
                                    onNext={navigator?.navigateToNext}
                                    onPrev={navigator?.navigateToPrev}
                                    updateNavState={updateNavState}
                                    isCompact
                                />
                            </div>
                        )}
                    </div>
                    <Spacing size={20} />

                    {!displayInSwiper && (
                        <div className={styles.list_large}>
                            {sortedTariffList.map((tariff) => (
                                <div
                                    ref={cardRef}
                                    className={styles.displayCard}
                                    key={`${tariff.tariffId}_${activePeriodTab}_${tariff.products.yearProduct.isFamily ? 'family' : ''}`}
                                >
                                    <TariffNestleCard
                                        tariff={tariff}
                                        isYear={activePeriodTab === ETabsName.year}
                                        onSuccessPayment={onSuccessPayment}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {displayInSwiper && (
                        <div className={styles.list} ref={cardRef}>
                            <Swiper
                                className={'swiper-padding'}
                                slidesPerView={'auto'}
                                spaceBetween={20}
                                threshold={20}
                                pagination={{
                                    clickable: true,
                                }}
                                onSwiper={(swiper) => {
                                    handleSetNavigator({
                                        navigateToNext: () => swiper?.slideTo(swiper?.activeIndex + 2),
                                        navigateToPrev: () => swiper?.slideTo(swiper?.activeIndex - 2),
                                        isFirst: () => swiper?.isBeginning,
                                        isLast: () => swiper?.isEnd,
                                    });
                                }}
                                onActiveIndexChange={updateNavState}
                                direction="horizontal"
                                mousewheel={{
                                    forceToAxis: true,
                                    sensitivity: 2,
                                }}
                                modules={[Mousewheel]}
                                onBeforeInit={(swiper) => {
                                    swiperRef.current = swiper;
                                }}
                            >
                                {sortedTariffList.map((tariff, idx) => (
                                    <SwiperSlide
                                        key={`${tariff.tariffId}_${activePeriodTab}_${
                                            tariff.products.yearProduct.isFamily ? 'family' : ''
                                        }`}
                                        virtualIndex={idx}
                                        className={styles.slide}
                                    >
                                        {() => {
                                            return (
                                                <div className={styles.carouselCard}>
                                                    <TariffNestleCard
                                                        tariff={tariff}
                                                        isYear={activePeriodTab === ETabsName.year}
                                                        onSuccessPayment={onSuccessPayment}
                                                    />
                                                </div>
                                            );
                                        }}
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    )}
                </>
            )}

            <Spacing size={isMobile ? 0 : 80} />
        </div>
    );
});

TariffsBlock.displayName = 'TariffsBlock';
